import axios from "axios";
import { useEffect, useState } from "react";
import Box from "elements/Box";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { useSnackbar } from "notistack";
import DateRange from "elements/DateRange";
import moment from "moment";
import ReportViewer from "./ReportViewer";
import { ApiRequest } from "GlobalFunctions";
import EventIcon from '@mui/icons-material/Event';
import AutocompleteField from "elements/AutocompleteField";

export default function RoutesReports(props: any) {
  const { setLoading } = props;

  const [loadingPopup, setLoadingPopup] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setLoading(false);
  }, []);

  const defaultValues: any = {
    date: null,
    user_id: null,
    guide_code: '',
  };

  const [exportType, setExportType] = useState("");
  const [formValues, setFormValues] = useState({ ...defaultValues });
  const [reportName, setReportName] = useState("");

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [data, setData] = useState([]);
  const [openViewer, setOpenViewer] = useState(false);

  const requiresDateOrGuide = exportType === 'by_user' || exportType === 'picking_summary';
  const hasDateRange = Boolean(formValues.date?.startDate && formValues.date?.endDate);
  const hasGuideCode = Boolean(formValues.guide_code?.trim());
  const hasPrimaryFilter = hasDateRange || hasGuideCode;

  const downloadImagesZip = async () => {
    if (!hasDateRange) {
      enqueueSnackbar('Debes seleccionar un rango de fechas para descargar imágenes', { variant: 'warning' });
      return;
    }

    setLoading(true);

    await ApiRequest({
      url: "/reports/downloadImagesZip",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        ...formValues,
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar(`ZIP generado: ${response.totalImages} imágenes de ${response.totalGuides} guías`, { variant: 'success' });
          window.open(response.fileUrl, '_blank');
        } else {
          enqueueSnackbar(response.message || 'Error al generar el ZIP', { variant: 'error' });
        }
        setLoading(false);
      },
    });
  };

  const getReport = async (download = "") => {
    if (requiresDateOrGuide && !hasPrimaryFilter) {
      enqueueSnackbar('Debes ingresar un número de guía o seleccionar un rango de fechas', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setLoadingPopup(true);

    await ApiRequest({
      url: download ? "/reports/exportRoutesWebReport" : "/reports/getReport",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        ...formValues,
        exportType,
        module: exportType === 'picking_summary' ? 'picking' : 'routes',
        download,
        report_type: exportType  // Para el endpoint de export
      },
      setResponse: (response: any) => {
        console.log("response", response);
        if(response.status === "success"){
          // Para reports de tabla (returns, partials, loans), pasar el objeto completo
          // Para by_user y by_guide, pasar solo los datos como antes
          if(exportType === 'returns' || exportType === 'partials' || exportType === 'loans') {
            setData(response);  // Objeto completo con tableHeaders
          } else if(exportType === 'picking_summary') {
            setData(response);  // Objeto completo con summary + data
          } else {
            setData(response.data);  // Solo el array de datos para by_user/by_guide
          }
          setOpenViewer(true);

          if(download === 'pdf') {
            console.log(response.pdfUrl);
            window.open(response.pdfUrl, '_blank');
          }
          if(download === 'excel'){
            window.open(response.fileUrl, '_blank');
          }
        }
        else{
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        setLoading(false);
        setLoadingPopup(false);
      },
    });
  };

  return (
    <>
      <div id="SupplierList" className="ModuleContainer">
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Reportes Rutas</h2>
              <span className="h2_subtitle">Selecciona el tipo de exportación</span>
            </div>
          </div>

          <Box id="SupplierListIn" className="box100">

            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Reporte</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={exportType}
                label="Reporte"
                name="export_type"
                onChange={(e: any) => {
                  setExportType(e.target.value);
                  setFormValues({ ...defaultValues });
                  setReportName(e.target.value);
                }}
              >
                <MenuItem value={"by_guide"}>Relación guía</MenuItem>
                <MenuItem value={"by_user"}>Relación por usuario</MenuItem>
                <MenuItem value={"download_images"}>Descarga de imágenes</MenuItem>
                <MenuItem value={"returns"}>Relación devoluciones</MenuItem>
                <MenuItem value={"partials"}>Relación parciales</MenuItem>
                <MenuItem value={"loans"}>Relación préstamos</MenuItem>
                <MenuItem value={"picking_summary"}>Reporte Picking</MenuItem>
              </Select>
            </FormControl>

            {requiresDateOrGuide ? (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', width: '100%' }}>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <DateRange
                    className="filters_datePicker"
                    label="Rango de fechas"
                    variant="outlined"
                    fullWidth
                    value={{
                      startDate: formValues.date?.startDate || null,
                      endDate: formValues.date?.endDate || null,
                    }}
                    format="MMMM DD, yyyy"
                    onChange={(e: any) => {
                      if (e.startDate && e.endDate) {
                        const startDate = moment(e.startDate).format("YYYY-MM-DD");
                        const endDate = moment(e.endDate).format("YYYY-MM-DD");

                        setFormValues({
                          ...formValues,
                          date: { startDate, endDate },
                        });
                        return;
                      }

                      setFormValues({
                        ...formValues,
                        date: null,
                      });
                    }}
                    variantDatePicker="side-by-side"
                  />
                </div>

                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <TextField
                    label="Número de guía"
                    variant="outlined"
                    fullWidth
                    value={formValues.guide_code}
                    onChange={(e) => setFormValues({ ...formValues, guide_code: e.target.value })}
                  />
                </div>
              </div>
            ) : exportType ? (
              <DateRange
                className="filters_datePicker"
                label="Date range"
                variant="outlined"
                fullWidth
                value={{
                  startDate: formValues.date?.startDate || null,
                  endDate: formValues.date?.endDate || null,
                }}
                format="MMMM DD, yyyy"
                onChange={(e: any) => {
                  if (e.startDate && e.endDate) {
                    const startDate = moment(e.startDate).format("YYYY-MM-DD");
                    const endDate = moment(e.endDate).format("YYYY-MM-DD");

                    setFormValues({
                      ...formValues,
                      date: { startDate, endDate },
                    });
                    return;
                  }

                  setFormValues({
                    ...formValues,
                    date: null,
                  });
                }}
                variantDatePicker="side-by-side"
              />
            ) : null}

            {formValues.date && exportType !== 'picking_summary' && exportType !== 'by_user' && (
              <AutocompleteField
                label="Usuario (Opcional)"
                endpoint="/users/listByRole"
                endpoint_params={{ 
                  role_ids: [9,10],
                  full_list: true
                }}
                value={formValues.user_id}
                onChange={(data: any) => {
                  console.log("data", data?.value);
                    setFormValues({...formValues, user_id: data?.value || null});
                }}
              />
            )}

            {exportType === 'picking_summary' && hasPrimaryFilter && (
              <AutocompleteField
                label="Usuario (Opcional)"
                endpoint="/reports/pickingUserList"
                value={formValues.user_id}
                onChange={(data: any) => {
                    setFormValues({...formValues, user_id: data?.value || null});
                }}
              />
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (exportType === 'download_images') {
                  downloadImagesZip();
                } else {
                  getReport();
                }
              }}
              startIcon={<CloudDownloadOutlinedIcon />}
            >
              {exportType === 'download_images' ? 'Descargar ZIP' : 'Ver'}
            </Button>

          </Box>
        </div>
      </div>

      <ReportViewer
        open={openViewer}
        setOpen={setOpenViewer}
        data={data}
        loading={loadingPopup}
        setLoading={setLoading}
        reportName={reportName}
        getReport={getReport}
        exportParams={formValues} // Pasar los parámetros del formulario para las exportaciones
      />
    </>
  );
}
